import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const IntercomArticlesModal = () => {
  const { t } = useTranslation(['knowledge_base', 'common']);
  const { isOpen, closeModal } = useModalStore();
  const navigate = useNavigate();
  const { scenario } = useParams<{ scenario: string }>();
  const queryClient = useQueryClient();

  const isModalOpen = isOpen(MODAL_NAMES.INTERCOM_ARTICLES_MODAL_ID);
  const integrations = queryClient.getQueryData<Integrations>(['integrations']);

  const hasToken = integrations?.intercom?.has_token;
  const hasArticles = integrations?.intercom?.has_articles;

  const importMutation = useMutation({
    mutationKey: [QueryKey.knowledgeBase.intercom.import],
  });

  const refreshMutation = useMutation({
    mutationKey: [QueryKey.knowledgeBase.intercom.refresh],
  });

  const deleteMutation = useMutation({
    mutationKey: [QueryKey.knowledgeBase.intercom.delete],
  });

  // Close modal when any mutation succeeds
  useEffect(() => {
    if (importMutation.isSuccess || refreshMutation.isSuccess || deleteMutation.isSuccess) {
      closeModal();
    }
  }, [importMutation.isSuccess, refreshMutation.isSuccess, deleteMutation.isSuccess, closeModal]);

  const handleNavigateToIntegrations = () => {
    closeModal();
    if (scenario) {
      navigate(`/i/${scenario}/${IndexPath.channels}`);
      return;
    }
    navigate(IndexPath.channels);
  };

  const isLoading =
    importMutation.isPending || refreshMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={isModalOpen} onOpenChange={closeModal}>
      <DialogContent
        className="w-lg"
        data-testid="intercom-articles-modal"
        closeButtonProps={{ 'data-testid': 'intercom-articles-back-button' }}
      >
        <div className="flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('modals.external_apps.intercom.modal_title')}</DialogTitle>
          </DialogHeader>

          <DialogDescription asChild>
            <div className="mt-2 space-y-4">
              {!hasToken ? (
                <>
                  <p>{t('modals.external_apps.intercom.not_connected')}</p>
                  <Alert variant="warning">
                    <AlertDescription>
                      <a
                        href="/#"
                        className="font-semibold underline text-foreground hover:!text-foreground-subtle"
                        onClick={(event) => {
                          event.preventDefault();
                          handleNavigateToIntegrations();
                        }}
                        data-testid="intercom-articles-go-to-integrations"
                      >
                        {t('modals.external_apps.intercom.go_to_integrations')}
                      </a>
                    </AlertDescription>
                  </Alert>
                </>
              ) : hasArticles ? (
                <>
                  <p>{t('modals.external_apps.intercom.already_imported')}</p>
                </>
              ) : (
                <>
                  <p>{t('modals.external_apps.intercom.description')}</p>
                  <p
                    className="text-sm text-foreground-subtle"
                    data-testid="intercom-articles-import-description"
                  >
                    {t('modals.external_apps.intercom.import_description')}
                  </p>
                </>
              )}
            </div>
          </DialogDescription>
        </div>

        <Separator />

        <DialogFooter className="flex flex-row !justify-between !items-center">
          <Button
            variant="secondary"
            type="button"
            onClick={closeModal}
            disabled={isLoading}
            data-testid="intercom-articles-cancel-button"
          >
            {t('common:cancel')}
          </Button>

          <div className="flex gap-2">
            {hasToken && hasArticles && (
              <>
                <Button
                  variant="danger"
                  type="button"
                  onClick={() => deleteMutation.mutate()}
                  disabled={isLoading}
                  data-testid="intercom-articles-delete-button"
                >
                  {deleteMutation.isPending ? (
                    <Spinner />
                  ) : (
                    t('modals.external_apps.intercom.delete_button')
                  )}
                </Button>
                <Button
                  variant="dark"
                  type="button"
                  onClick={() => refreshMutation.mutate()}
                  disabled={isLoading}
                  data-testid="intercom-articles-refresh-button"
                >
                  {refreshMutation.isPending ? (
                    <Spinner />
                  ) : (
                    t('modals.external_apps.intercom.refresh_button')
                  )}
                </Button>
              </>
            )}

            {hasToken && !hasArticles && (
              <Button
                variant="dark"
                type="button"
                onClick={() => importMutation.mutate()}
                disabled={isLoading}
                data-testid="intercom-articles-import-button"
              >
                {importMutation.isPending ? (
                  <Spinner />
                ) : (
                  t('modals.external_apps.intercom.import_button')
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
